import { useState, useEffect, useCallback } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Button from "../../components/ui/Button";
import SplineEditor from "../../components/charts/SplineEditor";
import useProjectStore from "../../store/projectStore";
import useSaveShortcut from "../../hooks/useSaveShortcut";
import useBladeReadonly from "../../hooks/useBladeReadonly";

import { useChartColors } from "../../chartTheme";

const DEFAULT_MULTIPLIERS = {
  HUB: 1.10, "SL-1": 1.08, "SL-2": 1.05, "SL-3": 1.03, SHROUD: 1.00,
};

// Topology-aware default CPs (constraints enforced)
function defaultCPs(topology) {
  return topology === "DoveTail"
    ? [[0, 0], [0, 8], [0.5, 8], [1, 6]]
    : [[0, 0], [0, 8], [0.5, 8], [1, 2], [1, 0]];
}

// ── Constraint logic ──────────────────────────────────────────────────────────
//
// RoundEdge:
//   CP[0]   = (0, 0)  — locked s + t
//   CP[1]   = (0, ?)  — locked s
//   CP[n-2] = (1, ?)  — locked s
//   CP[n-1] = (1, 0)  — locked s + t
//
// DoveTail:
//   CP[0]   = (0, 0)  — locked s + t
//   CP[1]   = (0, ?)  — locked s
//   CP[n-1] = (1, ?)  — locked s

function enforceCPConstraints(cps, topology) {
  if (!cps.length) return cps;
  const isRound = topology === "RoundEdge";
  const n = cps.length;
  const r = cps.map(cp => [
    Math.min(1, Math.max(0, cp[0])),
    Math.max(0, cp[1]),
  ]);
  r[0] = [0, 0];
  if (n > 1) r[1] = [0, r[1][1]];
  if (isRound) {
    r[n - 1] = [1, 0];
    if (n > 2) r[n - 2] = [1, r[n - 2][1]];
  } else {
    r[n - 1] = [1, r[n - 1][1]];
  }
  return r;
}

function isSLocked(i, n, isRound) {
  return i === 0 || i === 1 || i === n - 1 || (isRound && i === n - 2);
}

function isTLocked(i, n, isRound) {
  return i === 0 || (isRound && i === n - 1);
}

function isDeleteLocked(i, n, isRound) {
  return n <= 4 || i === 0 || i === 1 || i === n - 1 || (isRound && i === n - 2);
}

// ── Control point table ───────────────────────────────────────────────────────

function ThicknessTable({ controlPoints, onChange, topology }) {
  const isRound = topology === "RoundEdge";
  const n = controlPoints.length;

  const update = (i, col, val) => {
    const v = parseFloat(val);
    if (isNaN(v)) return;
    onChange(enforceCPConstraints(
      controlPoints.map((cp, j) => j === i ? (col === 0 ? [v, cp[1]] : [cp[0], v]) : cp),
      topology,
    ));
  };

  const remove = (i) => onChange(
    enforceCPConstraints(controlPoints.filter((_, j) => j !== i), topology)
  );

  const insertAfter = (i) => {
    const a = controlPoints[i], b = controlPoints[i + 1];
    const mid = [
      parseFloat(((a[0] + b[0]) / 2).toFixed(3)),
      parseFloat(((a[1] + b[1]) / 2).toFixed(2)),
    ];
    onChange(enforceCPConstraints(
      [...controlPoints.slice(0, i + 1), mid, ...controlPoints.slice(i + 1)],
      topology,
    ));
  };

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();
    const parsed = text.trim().split(/\r?\n/).map(line => {
      const p = line.split("\t");
      return [
        parseFloat(p[0]?.replace(",", ".") ?? ""),
        parseFloat(p[1]?.replace(",", ".") ?? ""),
      ];
    }).filter(([s, t]) => !isNaN(s) && !isNaN(t));
    if (parsed.length >= 4) onChange(enforceCPConstraints(parsed, topology));
  }, [topology, onChange]);

  return (
    <div onPaste={handlePaste}>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="w-6 border border-gray-200 px-1 py-1 text-gray-400 font-normal">#</th>
            <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">s [0–1]</th>
            <th className="border border-gray-200 px-2 py-1 text-gray-600 font-semibold text-left">t [mm]</th>
            <th className="w-12 border border-gray-200" />
          </tr>
        </thead>
        <tbody>
          {controlPoints.map((cp, i) => {
            const sLock = isSLocked(i, n, isRound);
            const tLock = isTLocked(i, n, isRound);
            const delLock = isDeleteLocked(i, n, isRound);
            return (
              <tr key={i} className={`${sLock || tLock ? "bg-gray-50/60" : "hover:bg-bh-50/40"}`}>
                <td className="border border-gray-200 px-1 py-0.5 text-gray-300 text-center select-none">
                  {i + 1}
                </td>
                <td className="border border-gray-200 p-0">
                  {sLock
                    ? <span className="block px-2 py-1 text-gray-400">{cp[0]}</span>
                    : <input type="number" step="0.01" min="0" max="1" value={cp[0]}
                        onChange={e => update(i, 0, e.target.value)}
                        className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50" />
                  }
                </td>
                <td className="border border-gray-200 p-0">
                  {tLock
                    ? <span className="block px-2 py-1 text-gray-400">{cp[1]}</span>
                    : <input type="number" step="0.1" min="0" value={cp[1]}
                        onChange={e => update(i, 1, e.target.value)}
                        className="w-full px-2 py-1 text-xs bg-transparent focus:outline-none focus:bg-bh-50" />
                  }
                </td>
                <td className="border border-gray-200 px-1 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {i < n - 1 && (
                      <button onClick={() => insertAfter(i)} title="Insert point after"
                        className="text-gray-300 hover:text-bh-400 transition-colors">
                        <PlusIcon className="h-3 w-3" />
                      </button>
                    )}
                    <button onClick={() => remove(i)} disabled={delLock} title="Delete point"
                      className="text-gray-300 hover:text-red-400 transition-colors disabled:opacity-20">
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-gray-400 text-xs mt-2 px-1">Paste from Excel (s | t columns)</p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function loadFromDesign(design) {
  const cls = design?.construction_lines;
  const topology = design?.topology || "RoundEdge";
  if (!cls?.length) return null;

  const ref = cls.find(cl => cl.thickness_spline?.control_points?.length);
  const rawCps = ref?.thickness_spline?.control_points ?? defaultCPs(topology);
  const cps = enforceCPConstraints(rawCps, topology);
  const degree = ref?.thickness_spline?.degree ?? 5;

  const multipliers = {};
  cls.forEach(cl => {
    const saved = cl.thickness_spline?.multipliers;
    if (saved && typeof saved === "object") {
      Object.assign(multipliers, saved);
    } else {
      multipliers[cl.name] = DEFAULT_MULTIPLIERS[cl.name] ?? 1.0;
    }
  });

  return { cps, degree, multipliers };
}

export default function ThicknessPage() {
  const { LINE_COLORS } = useChartColors();
  const { activeDesign, saveConstructionLines } = useProjectStore();
  const { isReadOnly } = useBladeReadonly();
  const topology = activeDesign?.topology || "RoundEdge";

  const initial = loadFromDesign(activeDesign);
  const [cps, setCpsRaw]     = useState(initial?.cps ?? defaultCPs(topology));
  const [degree, setDegree]  = useState(initial?.degree ?? 5);
  const [multipliers, setMultipliers] = useState(initial?.multipliers ?? DEFAULT_MULTIPLIERS);
  const [saving, setSaving]  = useState(false);

  // Always enforce constraints on the way in
  const setCps = useCallback((newCPs) => {
    setCpsRaw(enforceCPConstraints(newCPs, topology));
  }, [topology]);

  useEffect(() => {
    const loaded = loadFromDesign(activeDesign);
    if (loaded) {
      setCpsRaw(loaded.cps);
      setDegree(loaded.degree);
      setMultipliers(loaded.multipliers);
    }
  }, [activeDesign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!activeDesign) return;
    setSaving(true);
    try {
      const cls = activeDesign.construction_lines || [];
      const merged = cls.map(cl => ({
        ...cl,
        thickness_spline: { degree, control_points: cps, multipliers },
      }));
      await saveConstructionLines(activeDesign.id, merged);
    } catch { /* 403 or network error — stay silent, UI already blocked */ }
    finally {
      setSaving(false);
    }
  };
  useSaveShortcut(save, { disabled: saving || isReadOnly });

  const cls        = activeDesign?.construction_lines || [];
  const lineNames  = cls.length ? cls.map(cl => cl.name) : Object.keys(multipliers);

  const overlayLines = lineNames.map((name, i) => {
    const cl = cls.find(c => c.name === name);
    const isActive = !cl || cl.active !== false;
    return {
      name,
      control_points: cps.map(([s, t]) => [s, t * (multipliers[name] ?? 1.0)]),
      color: isActive ? LINE_COLORS[i % LINE_COLORS.length] : "#d1d5db",
    };
  });

  const topologyLabel = topology === "DoveTail" ? "Dove Tail" : "Round Edge";


  return (
    <div className="flex gap-0 h-full">

      {/* ── Left: stream surfaces + multipliers ── */}
      <div className="w-52 shrink-0 border-r border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stream surfaces</p>
        </div>
        <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {lineNames.map((name, i) => {
            const cl = cls.find(c => c.name === name);
            const isActive = !cl || cl.active !== false;
            return (
              <div key={name}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 ${!isActive ? "opacity-50" : ""}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: isActive ? LINE_COLORS[i % LINE_COLORS.length] : "#9ca3af" }} />
                <span className={`text-sm flex-1 min-w-0 truncate ${isActive ? "text-gray-700" : "text-gray-400"}`}>
                  {name}
                </span>
                <input type="number" step="0.01" min="0"
                  value={multipliers[name] ?? 1.0}
                  onChange={e => setMultipliers(m => ({ ...m, [name]: Number(e.target.value) }))}
                  className="w-14 text-xs text-right border border-gray-200 rounded px-1 py-0.5
                    focus:outline-none focus:border-bh-400 bg-white"
                  title={`${name} thickness multiplier`}
                />
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t border-gray-200 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 flex-1">B-spline degree</span>
            <input type="number" value={degree}
              onChange={e => setDegree(Number(e.target.value))}
              className="w-14 text-xs text-right border border-gray-200 rounded px-1 py-0.5
                focus:outline-none focus:border-bh-400 bg-white" />
          </div>
          <Button size="sm" className="w-full justify-center" onClick={save} loading={saving} disabled={isReadOnly} shortcut="Ctrl+S">
            Save
          </Button>
        </div>
      </div>

      {/* ── Centre: control point table ── */}
      <div className="w-64 shrink-0 border-r border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-800">Control points</p>
          <p className="text-xs text-gray-400">Shroud reference curve · {topologyLabel}</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          <ThicknessTable
            controlPoints={cps}
            onChange={setCps}
            topology={topology}
          />
        </div>
      </div>

      {/* ── Right: spline editor ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-800">Thickness Distribution</p>
          <p className="text-xs text-gray-400">
            s = bow length [0–1] · locked boundary CPs snap back when dragged
          </p>
        </div>
        <div className="flex-1 p-3">
          <SplineEditor
            key={topology}
            degree={degree}
            controlPoints={cps}
            onChange={setCps}
            xLabel="Bow length s [0–1]"
            yLabel="Thickness [mm]"
            color="#94a3b8"
            xRange={[0, 1]}
            allLines={overlayLines}
            activeIdx={-1}
          />
        </div>
      </div>

    </div>
  );
}
